import type { VisualShape, VisualState } from 'types'
import type { ModelRoute } from '../logic/smartRouting.js'
import {
  AGENT_SHAPES,
  MULTIMODAL_SHAPES,
  PRESENCE_SHAPES,
  RESEARCH_ANIMATION_SHAPES,
  getShapePriority,
} from './canvas-shape-taxonomy.js'

const WAVEFORM_PRIORITY_SHAPES = new Set<VisualShape>([
  'wave',
  'orb',
  'oscilloscope',
  'spectrum',
])

export interface DominantCanvasShapeOptions {
  hasResearch?: boolean
  hasRunningTool?: boolean
  runningToolName?: string
  sourceCount?: number
  citationCount?: number
  preferredWaveform?: VisualState['waveformMode']
}

export interface DominantCanvasShapeResult {
  shape: VisualShape
  waveformEnabled: boolean
  waveformMode: NonNullable<VisualState['waveformMode']>
  researchActive: boolean
}

export function resolveDominantCanvasShape(
  candidateShape: VisualShape,
  mode: VisualState['mode'],
  activeRoute: ModelRoute,
  webcamActive: boolean,
  options?: DominantCanvasShapeOptions,
): DominantCanvasShapeResult {
  const hasResearch = options?.hasResearch ?? false
  const hasRunningTool = options?.hasRunningTool ?? false
  const runningToolName = options?.runningToolName?.toLowerCase() ?? ''
  const sourceCount = options?.sourceCount ?? 0
  const citationCount = options?.citationCount ?? 0
  const preferredWaveform = options?.preferredWaveform ?? 'oscilloscope'

  let shape = candidateShape

  if (hasRunningTool) {
    if (runningToolName.includes('screen')) {
      shape = 'screenshare'
    } else if (runningToolName.includes('webcam') || runningToolName.includes('camera')) {
      shape = 'webcam'
    } else if (runningToolName.includes('booking') || runningToolName.includes('calendar')) {
      shape = 'functionPulse'
    } else {
      shape = mode === 'speaking' ? 'wave' : 'orb'
    }
  } else if (
    hasResearch &&
    getShapePriority(candidateShape) < getShapePriority(sourceCount > 0 ? 'constellation' : 'orb')
  ) {
    shape = sourceCount > 0 || citationCount > 1 ? 'constellation' : 'orb'
  } else if (candidateShape === 'wave' && mode === 'thinking') {
    shape = 'orb'
  }

  if (mode === 'thinking' && getShapePriority(shape) < getShapePriority('orb')) {
    shape = hasResearch && sourceCount > 0 ? 'constellation' : 'orb'
  } else if (mode === 'listening' && getShapePriority(shape) < getShapePriority('orb')) {
    shape = 'orb'
  } else if (mode === 'speaking' && getShapePriority(shape) < getShapePriority('wave')) {
    shape = sourceCount > 0 ? 'constellation' : 'wave'
  }

  if (webcamActive && getShapePriority(shape) < getShapePriority('webcam')) {
    shape = 'webcam'
  }

  if (activeRoute.id.includes('nano') && getShapePriority(shape) < getShapePriority('shield')) {
    shape = 'shield'
  }

  return {
    shape,
    waveformEnabled:
      mode !== 'idle' &&
      (WAVEFORM_PRIORITY_SHAPES.has(shape) || mode === 'speaking' || mode === 'listening'),
    waveformMode: preferredWaveform,
    researchActive: hasResearch,
  }
}

export function pickMergedCanvasShape(
  app: VisualState,
  context: VisualState,
  appIntentAllowed: boolean,
): VisualShape {
  if (!appIntentAllowed && PRESENCE_SHAPES.has(context.shape)) {
    return context.shape
  }

  if (MULTIMODAL_SHAPES.has(context.shape)) {
    return context.shape
  }

  if (AGENT_SHAPES.has(app.shape) && PRESENCE_SHAPES.has(context.shape)) {
    return app.shape
  }

  if (RESEARCH_ANIMATION_SHAPES.has(app.shape) && PRESENCE_SHAPES.has(context.shape)) {
    if (app.shape === 'molecule' || app.shape === 'neural_network') {
      return app.shape
    }
    if (context.shape === 'orb') {
      return app.shape
    }
  }

  const appPriority = getShapePriority(app.shape)
  const ctxPriority = getShapePriority(context.shape)

  if (appPriority > ctxPriority) return app.shape
  if (ctxPriority > appPriority) return context.shape

  if (PRESENCE_SHAPES.has(context.shape) && !PRESENCE_SHAPES.has(app.shape)) {
    return app.shape
  }

  return context.shape
}
